import type { LinkHTMLAttributes, MetaHTMLAttributes } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { useSelector } from 'react-redux'

import { useTranslate } from '../utils/translate'

/**
 * Sets document metadata.
 *
 * If `title` is not provided, it defaults to the shop title. If there is a
 * view error, the title will be set to a specific error title.
 *
 * Note: This component should only be used once per route view to avoid having
 * more than one `<title>` tag in the document. If you need to set specific
 * additional metadata, you can directly use `<meta>` and `<link>` tags.
 */
export function DocumentMeta({
  title,
  meta,
  links,
}: Readonly<{
  title?: string
  meta?: MetaHTMLAttributes<HTMLMetaElement>[]
  links?: LinkHTMLAttributes<HTMLLinkElement>[]
}>) {
  const viewError = useSelector<State, ImmutableMap | undefined>((state) => state.getIn(['view', 'error']))
  const error = useRouteError()
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const t = useTranslate('shop')

  let documentTitle: string = title || shop.get('title')

  // If there is an error, use a specific error document title.
  // Don’t use an error title for the special "shop closed" view error
  // (the error message is defined in error.ts in fusion-backend-ts).
  if (
    isRouteErrorResponse(error) ||
    (viewError && viewError.get('message') !== 'The requested shop is currently closed')
  ) {
    const errorTitle = t('views.storefrontView.errorPageSection.errorcode', {
      code: isRouteErrorResponse(error) ? error.status : viewError?.get('statusCode'),
    })
    documentTitle = `${errorTitle} - ${shop.get('title')}`
  }

  return (
    <>
      <title>{documentTitle}</title>
      {meta?.map((meta) => <meta key={`${meta.name || meta.property}${meta.content}`} {...meta} />)}
      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
      {links?.map((link) => <link key={link.rel! + link.href + link.hrefLang} {...link} />)}
    </>
  )
}
